import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Save, Plus } from 'lucide-react'
import { useAdScheduleTemplateById } from '@/hooks/useAdScheduleTemplateById'
import { useAllAdvertisements } from '@/hooks/useAllAdvertisements'
import { useAuth } from '@/hooks/useAuth'
import { adScheduleTemplateService } from '@/services/ad_schedule_template_service'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import SlotEditor from './components/SlotEditor'
import { AdScheduleTemplateSlotItemInput } from '@/services/ad_schedule_template_service'
import { logger } from '@magnetar/utils'

export default function EditTemplatePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const { template, isLoading, mutate } = useAdScheduleTemplateById({
    template_id: id || null,
  })
  // 模板新增廣告對話框只列「啟用中」的廣告，避免使用者把已停用的廣告加進新模板
  const { advertisements } = useAllAdvertisements({ isActive: true })

  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    is_active: true,
    duration_limit_enabled: true,
  })
  const [activeTab, setActiveTab] = useState('info')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (template) {
      setFormData({
        template_name: template.template_name,
        description: template.description || '',
        is_active: template.is_active,
        duration_limit_enabled: template.duration_limit_enabled ?? true,
      })
    }
  }, [template])

  const handleSaveInfo = async () => {
    if (!token || !id) return

    setIsSaving(true)
    try {
      const result = await adScheduleTemplateService.updateTemplate(parseInt(id), formData, token)
      if (result.success) {
        mutate()
      }
    } catch (error) {
      logger.error('更新失敗', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveSlot = async (
    slotNumber: number,
    slotName: string,
    playMinute: number,
    items: AdScheduleTemplateSlotItemInput[],
    isActive: boolean = true,
  ) => {
    if (!token || !id) return

    const result = await adScheduleTemplateService.upsertSlot(
      parseInt(id),
      slotNumber,
      { slot_name: slotName, play_minute: playMinute, items, is_active: isActive },
      token,
    )
    if (result.success) {
      mutate()
    }
  }

  const handleDeleteSlot = async (slotId: number) => {
    if (!token || !id) return

    const result = await adScheduleTemplateService.deleteSlot(parseInt(id), slotId, token)
    if (result.success) {
      mutate()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到模版</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  // 找出已使用的梯次編號（只計算啟用中的梯次）
  const usedSlotNumbers = template.slots
    .filter((slot) => slot.is_active)
    .map((slot) => slot.slot_number)
  const availableSlotNumbers = Array.from({ length: 12 }, (_, i) => i + 1).filter(
    (num) => !usedSlotNumbers.includes(num),
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">編輯廣告排班模版</h1>
          <p className="text-muted-foreground">編輯模版資訊和梯次設定</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info">基本資訊</TabsTrigger>
          <TabsTrigger value="slots">
            梯次管理
            <Badge variant="secondary" className="ml-2">
              {template.slots.filter((s) => s.is_active).length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>基本資訊</CardTitle>
              <CardDescription>更新模版的基本資訊</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template_name">模版名稱</Label>
                <Input
                  id="template_name"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  placeholder="輸入模版名稱"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">描述</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="輸入描述"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">啟用模版</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="duration_limit_enabled"
                  checked={formData.duration_limit_enabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, duration_limit_enabled: checked })
                  }
                />
                <Label htmlFor="duration_limit_enabled">啟用時長限制 (180秒)</Label>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveInfo} disabled={isSaving}>
                  <Save className="mr-2 h-4 w-4" />
                  儲存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="slots" className="space-y-4">
          {availableSlotNumbers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>新增梯次</CardTitle>
                <CardDescription>選擇要新增的梯次編號</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {availableSlotNumbers.map((num) => (
                    <Button
                      key={num}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleSaveSlot(num, `梯次 ${num}`, 0, [], true)
                      }}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      新增梯次 {num}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {template.slots
            .filter((slot) => slot.is_active)
            .sort((a, b) => a.slot_number - b.slot_number)
            .map((slot) => (
              <SlotEditor
                key={slot.slot_id}
                slot={slot}
                advertisements={advertisements}
                durationLimitEnabled={template.duration_limit_enabled ?? true}
                onSave={(name, playMinute, items) =>
                  handleSaveSlot(slot.slot_number, name, playMinute, items)
                }
                onDelete={() => handleDeleteSlot(slot.slot_id)}
              />
            ))}

          {template.slots.filter((s) => s.is_active).length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">尚未建立任何梯次</p>
                <p className="text-sm text-muted-foreground mt-2">點擊上方按鈕新增梯次</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
